import React, { useState, useMemo } from 'react';
import {
  Button,
  Popover,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
} from '@mui/material';
import { FilterList, Search as SearchIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

export interface StateOption {
  _id: string;
  name: string;
}

interface StateFilterButtonProps {
  label?: string;
  value: string | null;
  onChange: (stateId: string | null) => void;
  states: StateOption[];
}

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'hasValue',
})<{ hasValue?: boolean }>(({ theme, hasValue }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
  backgroundColor: hasValue ? theme.palette.primary.main : theme.palette.common.white,
  borderRadius: '9999px',
  borderWidth: '1px',
  borderStyle: 'solid',
  padding: '6px 16px',
  fontWeight: 500,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: hasValue ? theme.palette.primary.main : theme.palette.primary.light,
    color: hasValue ? theme.palette.primary.contrastText : theme.palette.text.primary,
  },
  '&.MuiButton-contained': {
    backgroundColor: hasValue ? theme.palette.primary.main : theme.palette.common.white,
    color: hasValue ? theme.palette.primary.contrastText : theme.palette.text.primary,
    boxShadow: 'none',
    borderWidth: '1px',
    borderStyle: 'solid',
    '&:hover': {
      backgroundColor: hasValue ? theme.palette.primary.dark : theme.palette.primary.light,
      boxShadow: 'none',
    },
  },
}));

export const StateFilterButton: React.FC<StateFilterButtonProps> = ({
  label = 'State',
  value,
  onChange,
  states,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchTerm('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const filteredStates = useMemo(() => {
    if (!searchTerm.trim()) return states;
    const term = searchTerm.trim().toLowerCase();
    return states.filter(
      (s) =>
        s.name.toLowerCase().includes(term) ||
        (s as { stateCode?: string }).stateCode?.toLowerCase().includes(term)
    );
  }, [states, searchTerm]);

  const selectedState = states.find((s) => s._id === value);

  return (
    <>
      <StyledButton
        hasValue={value !== null}
        variant={value !== null ? 'contained' : 'outlined'}
        onClick={handleClick}
        startIcon={<FilterList />}
        size="small"
        endIcon={
          value !== null ? (
            <CancelIcon
              fontSize="small"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            />
          ) : undefined
        }
      >
        {selectedState ? selectedState.name : label}
      </StyledButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 200, maxHeight: 360 } }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search states..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1 }}
          />
          <List dense disablePadding sx={{ overflow: 'auto', maxHeight: 280 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onChange(null);
                  handleClose();
                }}
              >
                <Checkbox edge="start" checked={value === null} tabIndex={-1} disableRipple />
                <ListItemText primary="All states" />
              </ListItemButton>
            </ListItem>
            {filteredStates.map((state) => (
              <ListItem key={state._id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    onChange(state._id);
                    handleClose();
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={value === state._id}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText primary={state.name} />
                </ListItemButton>
              </ListItem>
            ))}
            {filteredStates.length === 0 && searchTerm.trim() && (
              <ListItem disablePadding>
                <ListItemButton disabled>
                  <ListItemText primary="No states match" secondary={searchTerm} />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};
